//================================================================
//  Component: Delete User Modal ~ Child component of 'AdminUserTable.js'
//================================================================

//  Purpose: Remove user from roles document

//  Properties:
//    - openDeleteModal = {useState, contains the user object}
//    - setOpenDeleteModal = {useState used to determine if the side pane is open/closed}

//  Example:
//    <AdminUserTableDeleteUser
//      openDeleteModal={openDeleteModal}
//      setOpenDeleteModal={setOpenDeleteModal}
//    ></AdminUserTableDeleteUser>    

//================================================================

//Libraries
import React, { useContext, useState } from 'react';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../../../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../../../../Library/WriteDocument';

//Images
import errorIcon from '../../../../../../Components/Images/Icon_Error_Red.svg';
import LoadingIcon from '../../../../../../Components/Images/Image_Loading_Ripple.svg';
import Error from '../../../../../../Components/Images/Icon_Warning_Red.svg';


export default function AdminUserTableDeleteUser({
    documentid,
    openDeleteModal,
    setOpenDeleteModal,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'onload', 'pending', 'error'
    const [modalStatus, setModalStatus] = useState('onload');

    // Confirm the user should be deleted, by typing in 'REMOVE'
    const [confirmDelete, setConfirmDelete] = useState('');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submits changes to the table
    const deleteUser = (emailaddress) => {

        // Remove user from Firestore map
        const users = getRoles[documentid];
        delete users[emailaddress];

        // Write over the WHOLE document!
        const document = {
            'collectionid': documentid,
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'users': users
        };

        WriteDocument('roles', documentid, document, false).then(() => {

            setModalStatus('onload');
            setOpenDeleteModal(undefined);

            // Set success toast
            setToast({
                'type': 'success',
                'message': 'User Access successfully removed.',
            });

        }).catch((error) => {

            console.log('error', error);
            setModalStatus('error');

        });

    };

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (openDeleteModal === undefined) return null;

    //====================================================
    //  Pending
    //====================================================

    if (modalStatus === 'pending') {

        return (
            <div className='Modal-Background'>
                <dialog className='Warning-Modal-Container w-[600px]'>

                    <img className='w-[130px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                    <button className='Primary-Button w-fit self-center' onClick={() => {

                        setOpenDeleteModal(undefined);
                        setModalStatus('onload');

                    }}>
                        Cancel
                    </button>

                </dialog >
            </div>
        )

    }

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (modalStatus === 'onload') {

        return (
            <div className='Modal-Background'>

                {/* Modal Container */}
                <dialog className='Warning-Modal-Container'>

                    {/* ------------------------------- */}
                    {/*  Header                         */}
                    {/* ------------------------------- */}

                    <h4 className='pb-3 border-b-2 border-solid border-[#D8D8D8]'>Remove User Access</h4>

                    {/* ------------------------------- */}
                    {/*  Body                           */}
                    {/* ------------------------------- */}

                    <div className='flex flex-col gap-2 pb-4 border-b-2 border-solid border-[#D8D8D8]'>

                        {/* Type in REMOVE to confirm delete */}
                        <div className='flex flex-col gap-2'>
                            <label>Type <strong>REMOVE</strong> to confirm</label>
                            <input
                                className='Input-Field-Text'
                                onChange={(e) => setConfirmDelete(e.target.value)}
                            ></input>
                        </div>

                        <div className='flex flex-row gap-2 px-3 py-3 mt-2 bg-[#FCF4F6] border-1 border-[#C4314B] rounded'>
                            <img src={errorIcon} alt='Warning-Icon-Red'></img>
                            <div>
                                You are about to revoke <strong>{openDeleteModal?.givenname} {openDeleteModal?.surname}’s</strong> access. They will lose all permissions immediately.
                            </div>
                        </div>

                    </div>

                    {/* ------------------------------- */}
                    {/*  Buttons                        */}
                    {/* ------------------------------- */}

                    {/* Buttons */}
                    <div className='flex flex-row gap-2 pt-2'>

                        <button disabled={!(confirmDelete.toLocaleLowerCase().includes('remove'))} className='Warning-Button' onClick={() => deleteUser(openDeleteModal.emailaddress)}>Remove User</button>
                        <button
                            className='Secondary-Button'
                            style={{ color: '#424242', border: '1px solid #424242' }}
                            onClick={() => {

                                setOpenDeleteModal(undefined);
                                setModalStatus('onload');

                            }}
                        >
                            Cancel
                        </button>

                    </div>

                </dialog>

            </div>
        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else {

        return (
            <div className='Modal-Background'>

                {/* Modal Container */}
                <dialog className='Warning-Modal-Container justify-center items-center w-[600px]'>

                    <img className='my-1 w-[50px]' src={Error} alt='Error'></img>

                    <h4 className='mb-0'>Oops! Something went wrong.</h4>

                    <p className='text-center leading-[1.7]'>
                        An error occurred while we processed your request.
                    </p>

                    <button
                        className='Warning-Button'
                        onClick={() => {

                            setModalStatus('onload');
                            setOpenDeleteModal(undefined);

                        }}
                    >
                        Close
                    </button>

                </dialog>

            </div>
        )

    }

    //------------------------------------------------------

}
